import { useOs } from '@mantine/hooks';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Button, Divider, IconButton, Modal, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import NavigationLink from 'kerberus-components/src/Components/Link/NavigationLink';
import getCDNImagePath from 'kerberus-components/src/Utils/getCDNImagePath';

import { AppArray } from './AppData';

import type { OS } from '@mantine/hooks';

interface DownloadAppsModalProps {
    onClose: () => void;
    open: boolean;
}

export default function DownloadAppsModal({ onClose, open }: Readonly<DownloadAppsModalProps>) {
    const osType = useOs();
    const theme = useTheme();

    const currentPlatform = useMemo(() => {
        const osMap: Record<OS, string> = {
            android: 'Android',
            ios: 'iOS',
            linux: 'Windows', // Fallback for Linux users
            macos: 'iOS', // Mac users likely have iOS devices
            undetermined: 'Windows', // Default fallback
            windows: 'Windows',
        };

        const mappedPlatform = osMap[osType] || 'Windows';
        return AppArray.find((app) => app.name === mappedPlatform) || AppArray[0];
    }, [osType]);

    return (
        <Modal onClose={onClose} open={open}>
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: 24,
                    left: '50%',
                    maxHeight: '90vh',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    position: 'absolute',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { md: 650, xs: '90%' },
                }}
            >
                {/* Modal Header */}
                <Box
                    sx={{
                        alignItems: 'center',
                        bgcolor: 'primary.main',
                        borderRadius: '12px 12px 0 0',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 3,
                    }}
                >
                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                        <Avatar
                            alt="Renki Logo"
                            src={getCDNImagePath('renki/renkilogo.png')}
                            sx={{
                                backgroundColor: 'white',
                                border: '6px solid white',
                                borderRadius: 4,
                                height: 48,
                                mr: 2,
                                width: 48,
                            }}
                            variant="rounded"
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                }}
                                variant="h5"
                            >
                                Renki Apps
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                }}
                                variant="body2"
                            >
                                Companion apps for your devices
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.2)',
                            },
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Modal Content */}
                <Box sx={{ backgroundColor: 'white', maxHeight: '75vh', overflow: 'auto', p: 4 }}>
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            mb: 4,
                        }}
                        variant="subtitle1"
                    >
                        The Renki companion app allows you to register and manage your devices from your mobile phone or
                        desktop.
                    </Typography>

                    {/* Recommended Platform Section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            color="primary"
                            sx={{
                                fontWeight: 500,
                                mb: 1,
                            }}
                            variant="body2"
                        >
                            RECOMMENDED FOR YOUR DEVICE
                        </Typography>
                        <Box
                            sx={{
                                alignItems: 'center',
                                bgcolor: 'primary.50',
                                border: `2px solid ${theme.palette.primary.main}`,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: { sm: 'row', xs: 'column' },
                                p: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    mb: { sm: 0, xs: 2 },
                                    width: { sm: '50%', xs: '100%' },
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            color: 'primary.main',
                                        }}
                                        variant="h6"
                                    >
                                        {currentPlatform?.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                        variant="body2"
                                    >
                                        {currentPlatform?.description}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    alignItems: { sm: 'flex-end', xs: 'center' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: { sm: 'flex-end', xs: 'center' },
                                    width: { sm: '50%', xs: '100%' },
                                }}
                            >
                                {currentPlatform?.available ? (
                                    <NavigationLink to={currentPlatform.url}>
                                        {currentPlatform.badge ? (
                                            <Box
                                                alt={`Download on ${currentPlatform.name}`}
                                                component="img"
                                                src={getCDNImagePath(`logos/${currentPlatform.badge}`)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    height: 40,
                                                    mt: 1,
                                                    width: 'auto',
                                                }}
                                            />
                                        ) : (
                                            <Button
                                                color="primary"
                                                size="large"
                                                sx={{
                                                    backgroundColor: 'primary.main',
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    minWidth: 150,
                                                }}
                                                variant="contained"
                                            >
                                                Download
                                            </Button>
                                        )}
                                    </NavigationLink>
                                ) : (
                                    <>
                                        <Button
                                            color="primary"
                                            disabled
                                            size="large"
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                minWidth: 150,
                                            }}
                                            variant="contained"
                                        >
                                            Coming Soon
                                        </Button>
                                        {currentPlatform?.comingSoonText ? (
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                    mt: 1,
                                                }}
                                                variant="caption"
                                            >
                                                {currentPlatform.comingSoonText}
                                            </Typography>
                                        ) : null}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Other Platforms Section */}
                    <Typography
                        color="primary"
                        sx={{
                            fontWeight: 500,
                            mb: 2,
                        }}
                        variant="body2"
                    >
                        ALL AVAILABLE PLATFORMS
                    </Typography>
                    <Stack spacing={2}>
                        {AppArray.filter((app) => app.name !== currentPlatform?.name && app.type !== 'tv').map(
                            (app) => (
                                <Box
                                    key={app.name}
                                    sx={{
                                        alignItems: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        p: 2,
                                    }}
                                >
                                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                }}
                                                variant="subtitle1"
                                            >
                                                {app.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="body2"
                                            >
                                                {app.description}
                                            </Typography>
                                            {!app.available && app.comingSoonText ? (
                                                <Typography
                                                    sx={{
                                                        color: 'text.secondary',
                                                        display: 'block',
                                                    }}
                                                    variant="caption"
                                                >
                                                    {app.comingSoonText}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                    <Box>
                                        {app.available ? (
                                            <NavigationLink to={app.url}>
                                                {app.badge ? (
                                                    <Box
                                                        alt={`Download on ${app.name}`}
                                                        component="img"
                                                        src={getCDNImagePath(`logos/${app.badge}`)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            height: 40,
                                                            mt: 1,
                                                            width: 'auto',
                                                        }}
                                                    />
                                                ) : (
                                                    <Button
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            borderColor: 'primary.main',
                                                            borderRadius: 1.5,
                                                            color: 'primary.main',
                                                            fontWeight: 600,
                                                        }}
                                                        variant="outlined"
                                                    >
                                                        Download
                                                    </Button>
                                                )}
                                            </NavigationLink>
                                        ) : (
                                            <Button
                                                color="primary"
                                                disabled
                                                size="small"
                                                sx={{
                                                    borderRadius: 1.5,
                                                    fontWeight: 600,
                                                }}
                                                variant="outlined"
                                            >
                                                Coming Soon
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            ),
                        )}
                    </Stack>

                    <Typography
                        color="primary"
                        sx={{
                            fontWeight: 500,
                            my: 2,
                        }}
                        variant="body2"
                    >
                        Smart TV Apps
                    </Typography>
                    <Stack spacing={2}>
                        {AppArray.filter((app) => app.name !== currentPlatform?.name && app.type === 'tv').map(
                            (app) => (
                                <Box
                                    key={app.name}
                                    sx={{
                                        alignItems: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        p: 2,
                                    }}
                                >
                                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                        <Box
                                            sx={{
                                                alignItems: 'center',
                                                backgroundColor: theme.palette.bgWhite.main,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 2,
                                                display: 'flex',
                                                height: 45,
                                                justifyContent: 'center',
                                                mr: 2,
                                                width: 45,
                                            }}
                                        >
                                            {app.icon}
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                }}
                                                variant="subtitle1"
                                            >
                                                {app.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                                variant="body2"
                                            >
                                                {app.description}
                                            </Typography>
                                            {!app.available && app.comingSoonText ? (
                                                <Typography
                                                    sx={{
                                                        color: 'text.secondary',
                                                        display: 'block',
                                                    }}
                                                    variant="caption"
                                                >
                                                    {app.comingSoonText}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                    <Box>
                                        {app.available ? (
                                            <NavigationLink to={app.url}>
                                                {app.badge ? (
                                                    <Box
                                                        alt={`Download on ${app.name}`}
                                                        component="img"
                                                        src={getCDNImagePath(`badges/${app.badge}`)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            height: 40,
                                                            width: 'auto',
                                                        }}
                                                    />
                                                ) : (
                                                    <Button
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            borderColor: 'primary.main',
                                                            borderRadius: 1.5,
                                                            color: 'primary.main',
                                                            fontWeight: 600,
                                                        }}
                                                        variant="outlined"
                                                    >
                                                        Download
                                                    </Button>
                                                )}
                                            </NavigationLink>
                                        ) : (
                                            <Button
                                                color="primary"
                                                disabled
                                                size="small"
                                                sx={{
                                                    borderRadius: 1.5,
                                                    fontWeight: 600,
                                                }}
                                                variant="outlined"
                                            >
                                                Coming Soon
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            ),
                        )}
                    </Stack>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                            sx={{
                                color: 'text.secondary',
                            }}
                            variant="caption"
                        >
                            All apps are free to download. Requires a Renki account to use.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}
